import { EventBus } from "../../contexts";
import { logger, LOGS } from "../../utils";
import { generateUUID } from "../../utils/uuid/generateUUID";
import { ApiManager } from "../api";
import { BannerResponseDTO } from "../api/types/apiResponse";
import { BannerData, BannerSource } from "./types/banner";

/** Écart minimal entre deux revérifications déclenchées par le battement du polling. */
const PERIODIC_RECHECK_GUARD_MS = 5 * 60 * 1000;

/**
 * @class BannerManager
 * ═══════════════════════════════════════════════════════════════════════════
 * Classe gérant les différentes bannières à afficher en fonction des event
 */
export class BannerManager {
    // #region Properties

    private eventBus: EventBus;
    private apiManager: ApiManager;
    private _currentBanner: BannerData | null = null;
    private _currentBannerSource: BannerSource | null = null;
    private _refreshInFlight: {
        source: BannerSource | null;
        promise: Promise<void>;
    } | null = null;
    private _lastPeriodicRecheckAt = 0;
    // Comparer la seule source ne distingue pas « inchangée » de « effacée puis relevée à l'identique ».
    private _bannerGeneration = 0;

    // #endregion Properties

    constructor(eventBus: EventBus, apiManager: ApiManager) {
        this.eventBus = eventBus;
        this.apiManager = apiManager;
    }

    // #region Initialisation

    // #endregion Initialisation

    // #region Initialisation and cleanup

    /**
     * @method cleanup
     * Cleans up all resources and stops all active processes for the BannerManager
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {void}
     * @output {Promise<void>}
     * @public
     */
    public async _cleanup(): Promise<void> {
        logger.info(
            LOGS.BannerManager,
            "BannerManager.cleanup() - Starting cleanup process",
        );

        // Phase 1: Stop all active processes and timers
        try {
            logger.info(
                LOGS.BannerManager,
                "  Phase 1: Stopping active processes",
            );

            // No timers, intervals, or polling to stop for BannerManager
            // All operations are event-based
        } catch (error) {
            logger.error(
                LOGS.BannerManager,
                `BannerManager.cleanup() - Error in Phase 1: ${error}`,
            );
        }

        // Phase 2: Clear internal state and caches
        try {
            logger.info(
                LOGS.BannerManager,
                "  Phase 2: Clearing internal state",
            );

            this._currentBanner = null;
            this._currentBannerSource = null;
        } catch (error) {
            logger.error(
                LOGS.BannerManager,
                `BannerManager.cleanup() - Error in Phase 2: ${error}`,
            );
        }

        logger.info(
            LOGS.BannerManager,
            "BannerManager.cleanup() - Cleanup completed successfully",
        );
    }

    // #endregion Initialisation and cleanup

    // #region Event Handlers

    public _registerEvents(): void {
        logger.info(LOGS.BannerManager, "_registerEvents()");
        this["max-analysed-mails-reached_handler"] =
            this["max-analysed-mails-reached_handler"].bind(this);
        this["user-license-updated_handler"] =
            this["user-license-updated_handler"].bind(this);
        this["mails-polling-tick_handler"] =
            this["mails-polling-tick_handler"].bind(this);

        this.eventBus.on(
            "max-analysed-mails-reached",
            this["max-analysed-mails-reached_handler"],
        );
        this.eventBus.on(
            "user-license-updated",
            this["user-license-updated_handler"],
        );
        this.eventBus.on(
            "mails-polling-tick",
            this["mails-polling-tick_handler"],
        );
    }

    private async "max-analysed-mails-reached_handler"(): Promise<void> {
        await this._verifyandDisplayMaximumAnalysedMailsReachedBanner();
    }

    private async "user-license-updated_handler"(): Promise<void> {
        await this._checkAndEmitOrgHoldBanner();
    }

    // Le quota peut se libérer sans qu'aucun retour au premier plan ne survienne.
    private async "mails-polling-tick_handler"(): Promise<void> {
        if (!this._currentBannerSource) return;

        const now = Date.now();
        if (now - this._lastPeriodicRecheckAt < PERIODIC_RECHECK_GUARD_MS) {
            return;
        }

        this._lastPeriodicRecheckAt = now;

        await this.refreshBanner();
    }

    // #endregion Event Handlers

    // #region Public Methods
    // Faire autant de method pour les banner que de cas présent dans l'US

    /**
     * @method getCurrentBanner
     * Retourne la dernière bannière reçue depuis le backend, ou null si aucune.
     * Permet au front de récupérer la bannière de startup même si l'event 'banner-updated'
     * a été émis avant que le DashboardLayout ne soit monté.
     * ═══════════════════════════════════════════════════════════════════════════
     * @output {BannerData | null}
     */
    public getCurrentBanner(): BannerData | null {
        logger.info(LOGS.BannerManager, "getCurrentBanner()");
        return this._currentBanner;
    }

    /**
     * @method refreshBanner
     * Revérifie la bannière affichée et l'efface si elle n'a plus lieu d'être.
     * ═══════════════════════════════════════════════════════════════════════════
     * Si sa source ne s'applique plus, l'autre est vérifiée avant d'effacer : sans quoi
     * une suspension masquée par une bannière quota disparaîtrait avec elle.
     * Sans bannière affichée, no-op — lever reste piloté par les events. Échec de
     * l'appel : bannière conservée.
     * @output {Promise<void>}
     * @public
     */
    public async refreshBanner(): Promise<void> {
        const source = this._currentBannerSource;

        // Dédupliqué par source : fusionner deux sources différentes ferait sauter
        // le rafraîchissement de la seconde.
        if (this._refreshInFlight?.source === source) {
            return this._refreshInFlight.promise;
        }

        const promise = this._doRefreshBanner(source).finally(() => {
            if (this._refreshInFlight?.promise === promise) {
                this._refreshInFlight = null;
            }
        });

        this._refreshInFlight = { source, promise };

        return promise;
    }

    /** Corps du rafraîchissement — passer par {@link refreshBanner}, qui déduplique. */
    private async _doRefreshBanner(source: BannerSource | null): Promise<void> {
        if (!source) {
            return;
        }

        const generation = this._bannerGeneration;

        try {
            const dto = await this._fetchQuotaAware(source);

            // Après l'await : la bannière a pu changer, disparaître, ou être relevée à l'identique.
            if (this._bannerGeneration !== generation) {
                return;
            }

            if (dto) {
                this._emitAndStoreBanner(this._buildBannerFromDTO(dto), source);
                return;
            }

            const other: BannerSource =
                source === "max-analysed-mails"
                    ? "org-hold"
                    : "max-analysed-mails";
            const otherDto = await this._fetchQuotaAware(other);

            if (this._bannerGeneration !== generation) {
                return;
            }

            if (otherDto) {
                this._emitAndStoreBanner(
                    this._buildBannerFromDTO(otherDto),
                    other,
                );
                return;
            }

            logger.info(
                LOGS.BannerManager,
                `refreshBanner - no banner applies, clearing (was ${source})`,
            );
            this._clearBanner();
        } catch (error) {
            // Non effaçant : `null` seul signifie « plus de bannière ».
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            logger.error(
                LOGS.BannerManager,
                `refreshBanner - ${source} check failed, banner kept: ${errorMessage}`,
            );
        }
    }

    // Le fait porteur est la réponse `null` de l'endpoint quota, pas l'effacement de la bannière.
    private async _fetchQuotaAware(
        source: BannerSource,
    ): Promise<BannerResponseDTO | null> {
        const dto = await this._fetchBannerFor(source);

        if (source === "max-analysed-mails" && !dto) {
            this.eventBus.emit("max-analysed-mails-released");
        }

        return dto;
    }

    /** Table source → endpoint : une entrée par bannière, pas une condition de plus. */
    private _fetchBannerFor(
        source: BannerSource,
    ): Promise<BannerResponseDTO | null> {
        const endpoints: Record<
            BannerSource,
            () => Promise<BannerResponseDTO | null>
        > = {
            "max-analysed-mails": () =>
                this.apiManager.getMaxAnalysedMailsBanner(),
            "org-hold": () => this.apiManager.getOrganizationHoldBanner(),
        };

        return endpoints[source]();
    }

    /**
     * @method _verifyandDisplayMaximumAnalysedMailsReachedBanner
     * Interroge l'API pour vérifier si le quota mensuel de mails analysés est atteint,
     * puis émet 'banner-updated' avec le message approprié (B2C ou B2B).
     * ═══════════════════════════════════════════════════════════════════════════
     * @remarks Sans effet si l'API retourne null (quota non atteint) ou si apiManager n'est pas défini
     * @output {Promise<void>}
     * @private
     */
    private async _verifyandDisplayMaximumAnalysedMailsReachedBanner(): Promise<void> {
        try {
            const dto = await this.apiManager.getMaxAnalysedMailsBanner();
            if (!dto) {
                logger.info(
                    LOGS.BannerManager,
                    "_verifyandDisplayMaximumAnalysedMailsReachedBanner - Quota not reached (backend returned null)",
                );
                return;
            }

            const bannerDataBuild = this._buildBannerFromDTO(dto);
            this._emitAndStoreBanner(bannerDataBuild, "max-analysed-mails");
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            logger.error(
                LOGS.BannerManager,
                `Error in _verifyandDisplayMaximumAnalysedMailsReachedBanner: ${errorMessage}`,
            );
        }
    }

    /**
     * @method _checkAndEmitOrgHoldBanner
     * Vérifie si l'organisation de l'utilisateur est suspendue (HOLD) et émet 'banner-updated'
     * avec le message approprié selon le rôle (admin vs utilisateur simple).
     * ═══════════════════════════════════════════════════════════════════════════
     * @remarks Ignoré si l'utilisateur n'est pas de type B2B (ENTERPRISE_USER / ENTERPRISE_MANAGER)
     * @output {Promise<void>}
     * @private
     */
    private async _checkAndEmitOrgHoldBanner(): Promise<void> {
        try {
            const dto = await this.apiManager.getOrganizationHoldBanner();
            if (!dto) {
                logger.info(
                    LOGS.BannerManager,
                    "_checkAndEmitOrgHoldBanner - Org not on hold (backend returned null)",
                );
                return;
            }

            const bannerDataBuild = this._buildBannerFromDTO(dto);
            this._emitAndStoreBanner(bannerDataBuild, "org-hold");
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            logger.error(
                LOGS.BannerManager,
                `Error in _checkAndEmitOrgHoldBanner: ${errorMessage}`,
            );
        }
    }
    // #endregion

    // #region Private Methods

    /**
     * @method _emitAndStoreBanner
     * Stocke la bannière dans le cache (_currentBanner) et émet 'banner-updated'.
     * Garantit que getCurrentBanner() retourne toujours la dernière bannière,
     * même si le front n'était pas encore monté au moment de l'émission.
     * ═══════════════════════════════════════════════════════════════════════════
     * @param banner - BannerData à stocker et émettre
     * @param source - provenance, relue par refreshBanner()
     * @private
     */
    private _emitAndStoreBanner(
        banner: BannerData,
        source: BannerSource,
    ): void {
        this._currentBanner = banner;
        this._currentBannerSource = source;
        this._bannerGeneration += 1;
        this.eventBus.emit("banner-updated", banner);
    }

    /** Efface la bannière et l'annonce via 'banner-updated' avec null. */
    private _clearBanner(): void {
        this._currentBanner = null;
        this._currentBannerSource = null;
        this._bannerGeneration += 1;
        this.eventBus.emit("banner-updated", null);
    }

    /**
     * @method _buildBannerFromDTO
     * Mappe un BannerResponseDTO vers un BannerData exploitable par le front.
     * Attache un callToActionCallback si callToActionUrl est fourni par le backend.
     * ═══════════════════════════════════════════════════════════════════════════
     * @param dto - BannerResponseDTO retourné par le backend
     * @output {BannerData}
     * @private
     */
    private _buildBannerFromDTO(dto: BannerResponseDTO): BannerData {
        return {
            ...dto,
            id: dto.id ?? generateUUID(),
            ...(dto.callToActionUrl
                ? {
                      callToActionCallback: () =>
                          logger.info(
                              LOGS.BannerManager,
                              `CTA navigate to: ${dto.callToActionUrl}`,
                          ), // TODO routing front
                  }
                : {}),
        };
    }

    // #endregion
}
